import React, { useState, useMemo } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const TextReplacerPage = () => {
  const [inputText, setInputText] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [useRegex, setUseRegex] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [replaceAll, setReplaceAll] = useState(true);
  const [multilineReplacements, setMultilineReplacements] = useState([
    { find: '', replace: '', enabled: true }
  ]);
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'

  // Single replacement result
  const singleReplaceResult = useMemo(() => {
    if (!inputText || !findText) {
      return { 
        result: inputText, 
        count: 0, 
        error: null,
        matches: []
      };
    }

    try {
      let searchPattern;
      let flags = '';
      
      if (useRegex) {
        if (!caseSensitive) flags += 'i';
        if (replaceAll) flags += 'g';
        searchPattern = new RegExp(findText, flags);
      } else {
        const escapedFind = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (!caseSensitive) flags += 'i';
        if (replaceAll) flags += 'g';
        searchPattern = new RegExp(escapedFind, flags);
      }

      // Find all matches for highlighting
      const matches = [];
      let match;
      const globalPattern = new RegExp(searchPattern.source, searchPattern.flags.includes('g') ? searchPattern.flags : searchPattern.flags + 'g');
      
      while ((match = globalPattern.exec(inputText)) !== null) {
        matches.push({
          text: match[0],
          index: match.index,
          length: match[0].length
        });
        
        if (match.index === globalPattern.lastIndex) {
          globalPattern.lastIndex++;
        }
      }

      const result = inputText.replace(searchPattern, replaceText);
      const count = matches.length;

      return { result, count, error: null, matches };
    } catch (error) {
      return { 
        result: inputText, 
        count: 0, 
        error: error.message,
        matches: []
      };
    }
  }, [inputText, findText, replaceText, useRegex, caseSensitive, replaceAll]);

  // Bulk replacement result
  const bulkReplaceResult = useMemo(() => {
    if (!inputText) {
      return { 
        result: inputText, 
        totalCount: 0, 
        replacements: [],
        error: null
      };
    }

    try {
      let result = inputText;
      let totalCount = 0;
      const replacements = [];

      const enabledReplacements = multilineReplacements.filter(r => r.enabled && r.find);

      for (const replacement of enabledReplacements) {
        const beforeLength = result.length;
        let searchPattern;
        
        if (useRegex) {
          const flags = caseSensitive ? 'g' : 'gi';
          searchPattern = new RegExp(replacement.find, flags);
        } else {
          const escapedFind = replacement.find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const flags = caseSensitive ? 'g' : 'gi';
          searchPattern = new RegExp(escapedFind, flags);
        }

        const matches = (result.match(searchPattern) || []).length;
        result = result.replace(searchPattern, replacement.replace);
        
        if (matches > 0) {
          replacements.push({
            find: replacement.find,
            replace: replacement.replace,
            count: matches
          });
          totalCount += matches;
        }
      }

      return { result, totalCount, replacements, error: null };
    } catch (error) {
      return { 
        result: inputText, 
        totalCount: 0, 
        replacements: [],
        error: error.message
      };
    }
  }, [inputText, multilineReplacements, useRegex, caseSensitive]);

  const highlightMatches = (text, matches) => {
    if (!matches.length) return text;

    const parts = [];
    let lastIndex = 0;

    matches.forEach((match, i) => {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`before-${i}`}>
            {text.substring(lastIndex, match.index)}
          </span>
        );
      }

      // Add highlighted match
      parts.push(
        <span
          key={`match-${i}`}
          style={{
            backgroundColor: '#ffeb3b',
            padding: '2px 4px',
            borderRadius: '3px',
            fontWeight: 'bold'
          }}
        >
          {match.text}
        </span>
      );

      lastIndex = match.index + match.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key="after">
          {text.substring(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  const addReplacement = () => {
    setMultilineReplacements([
      ...multilineReplacements,
      { find: '', replace: '', enabled: true }
    ]);
  };

  const removeReplacement = (index) => {
    if (multilineReplacements.length > 1) {
      setMultilineReplacements(multilineReplacements.filter((_, i) => i !== index));
    }
  };

  const updateReplacement = (index, field, value) => {
    const updated = [...multilineReplacements];
    updated[index][field] = value;
    setMultilineReplacements(updated);
  };

  const loadSampleText = () => {
    setInputText(`Hello World! This is a sample text for testing.
The quick brown fox jumps over the lazy dog.
This text contains multiple lines and various words.
You can use this to test find and replace functionality.
Replace "text" with "content" or "fox" with "cat".
Email addresses like test@example.com can also be replaced.
Phone numbers: (555) 123-4567, 555.987.6543
Dates: 2024-01-15, 12/25/2023, Jan 1st 2024`);
  };

  const clearAll = () => {
    setInputText('');
    setFindText('');
    setReplaceText('');
    setMultilineReplacements([{ find: '', replace: '', enabled: true }]);
  };

  const copyResult = () => {
    const result = activeTab === 'single' ? singleReplaceResult.result : bulkReplaceResult.result;
    navigator.clipboard.writeText(result).then(() => {
      alert('Result copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  const currentResult = activeTab === 'single' ? singleReplaceResult : bulkReplaceResult;

  return (
    <div>
      <SEO 
        title="Text Replacer - Find and Replace Text Online" 
        description="Free online text replacer tool. Find and replace text with regex support, case sensitivity options, and bulk replacement features. Perfect for text editing and processing."
        keywords="text replacer, find and replace, text editor, regex replace, bulk replace, text processing, string replace"
      />
      
      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Text Replacer</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Find and replace text with advanced options including regex support and bulk replacements.
        </p>
        
        <AdUnit />

        {/* Tab Navigation */}
        <div className="tab-navigation" style={{ 
          display: 'flex', 
          marginBottom: '20px',
          borderBottom: '1px solid #ddd'
        }}>
          <button
            onClick={() => setActiveTab('single')}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: activeTab === 'single' ? '3px solid #007bff' : '3px solid transparent',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'single' ? 'bold' : 'normal',
              color: activeTab === 'single' ? '#007bff' : '#666'
            }}
          >
            Single Replace
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: activeTab === 'bulk' ? '3px solid #007bff' : '3px solid transparent',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'bulk' ? 'bold' : 'normal',
              color: activeTab === 'bulk' ? '#007bff' : '#666'
            }}
          >
            Bulk Replace
          </button>
        </div>

        {/* Controls */}
        <div className="controls-section" style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          alignItems: 'center', 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={useRegex}
                onChange={(e) => setUseRegex(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Use Regex
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Case Sensitive
            </label>
          </div>

          {activeTab === 'single' && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={replaceAll}
                  onChange={(e) => setReplaceAll(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Replace All Occurrences
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            <button
              onClick={loadSampleText}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Load Sample
            </button>
            <button
              onClick={clearAll}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear All
            </button>
            <button
              onClick={copyResult}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Copy Result
            </button>
          </div>
        </div>

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Input Section */}
          <div className="input-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Input & Replace Settings</h2>
            
            {/* Input Text */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Input Text:
                {inputText && (
                  <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
                    ({inputText.length} characters)
                  </span>
                )}
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter your text here..."
                style={{
                  width: '100%',
                  height: '200px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Single Replace Mode */}
            {activeTab === 'single' && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Find:
                  </label>
                  <input
                    type="text"
                    value={findText}
                    onChange={(e) => setFindText(e.target.value)}
                    placeholder={useRegex ? "Enter regex pattern..." : "Enter text to find..."}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Replace with:
                  </label>
                  <input
                    type="text"
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    placeholder="Enter replacement text..."
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </>
            )}

            {/* Bulk Replace Mode */}
            {activeTab === 'bulk' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '500' }}>Find & Replace Pairs:</label>
                  <button
                    onClick={addReplacement}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    + Add Pair
                  </button>
                </div>
                
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                  {multilineReplacements.map((replacement, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      alignItems: 'center', 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="checkbox"
                        checked={replacement.enabled}
                        onChange={(e) => updateReplacement(index, 'enabled', e.target.checked)}
                      />
                      <input
                        type="text"
                        value={replacement.find}
                        onChange={(e) => updateReplacement(index, 'find', e.target.value)}
                        placeholder="Find..."
                        style={{
                          flex: 1,
                          padding: '6px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '12px',
                          fontFamily: 'monospace'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#666' }}>→</span>
                      <input
                        type="text"
                        value={replacement.replace}
                        onChange={(e) => updateReplacement(index, 'replace', e.target.value)}
                        placeholder="Replace..."
                        style={{
                          flex: 1,
                          padding: '6px',
                          border: '1px solid #ddd',
                          borderRadius: '3px',
                          fontSize: '12px',
                          fontFamily: 'monospace'
                        }}
                      />
                      {multilineReplacements.length > 1 && (
                        <button
                          onClick={() => removeReplacement(index)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview with highlights */}
            {inputText && activeTab === 'single' && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ fontSize: '1rem', marginBottom: '8px' }}>Preview with Highlights:</h3>
                <div style={{
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {highlightMatches(inputText, singleReplaceResult.matches)}
                </div>
              </div>
            )}
          </div>

          {/* Result Section */}
          <div className="result-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Result</h2>
            
            {/* Statistics */}
            <div style={{ 
              padding: '15px', 
              backgroundColor: currentResult.error ? '#f8d7da' : '#e8f5e8',
              borderRadius: '8px',
              marginBottom: '15px',
              border: `2px solid ${currentResult.error ? '#dc3545' : '#28a745'}`
            }}>
              {currentResult.error ? (
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#721c24', marginBottom: '5px' }}>
                    ❌ Error
                  </div>
                  <div style={{ fontSize: '12px', color: '#721c24' }}>
                    {currentResult.error}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724', marginBottom: '5px' }}>
                    ✅ {activeTab === 'single' ? 
                      `${currentResult.count} replacement${currentResult.count !== 1 ? 's' : ''} made` :
                      `${currentResult.totalCount} total replacements made`
                    }
                  </div>
                  {activeTab === 'bulk' && currentResult.replacements.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#155724' }}>
                      {currentResult.replacements.map((r, i) => (
                        <div key={i}>
                          "{r.find}" → "{r.replace}" ({r.count} times)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Result Text */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Output Text:
                {currentResult.result && (
                  <span style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
                    ({currentResult.result.length} characters)
                  </span>
                )}
              </label>
              <textarea
                value={currentResult.result}
                readOnly
                style={{
                  width: '100%',
                  height: '400px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa'
                }}
              />
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>How to Use Text Replacer</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Single Replace</h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', paddingLeft: '20px' }}>
                <li>Enter text to find and replacement text</li>
                <li>Choose to replace first occurrence or all occurrences</li>
                <li>Use regex for advanced pattern matching</li>
                <li>Preview highlights show what will be replaced</li>
              </ul>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Bulk Replace</h3>
              <ul style={{ fontSize: '14px', lineHeight: '1.6', color: '#666', paddingLeft: '20px' }}>
                <li>Add multiple find/replace pairs</li>
                <li>Enable/disable individual replacements</li>
                <li>Replacements are applied in order</li>
                <li>Perfect for multiple text transformations</li>
              </ul>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Regex Examples</h3>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.6', color: '#666' }}>
                <div><code>\d+</code> - Match numbers</div>
                <div><code>\w+@\w+\.\w+</code> - Match emails</div>
                <div><code>^\s+|\s+$</code> - Match leading/trailing spaces</div>
                <div><code>\b\w{4}\b</code> - Match 4-letter words</div>
                <div><code>(\d{4})-(\d{2})-(\d{2})</code> - Match dates</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 15px;
          }
          
          .controls-section {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .controls-section > div:last-child {
            margin-left: 0 !important;
            margin-top: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default TextReplacerPage; 